import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, ExternalLink, Lock } from 'lucide-react';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useNavigate } from 'react-router-dom';
import { CareerAnalysisData, safeParseCareerAnalysis } from '../../types/analysis';

interface JobRecommendationsProps {
  userAnswers: any;
  analysis: CareerAnalysisData | null;
}

const JobRecommendations: React.FC<JobRecommendationsProps> = ({ userAnswers, analysis }) => {
  const { plan } = useSubscriptionStore();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Array<{title: string, company?: string, url: string, salary?: string}>>([]);
  
  const isPremium = plan === 'premium';
  
  useEffect(() => {
    if (!analysis) return;
    
    // Obsługa parsowania zarówno obiektu jak i stringa JSON
    const parsedAnalysis = typeof analysis === 'string'
      ? safeParseCareerAnalysis(analysis)
      : analysis;
    
    if (!parsedAnalysis || !parsedAnalysis.sciezkiKariery) return;
    
    // Generujemy oferty pracy na podstawie ścieżek kariery
    const generatedJobs = parsedAnalysis.sciezkiKariery.map(career => {
      const careerName = career.nazwa || 'Praca';
      return {
        title: careerName,
        company: 'Najlepsze oferty',
        url: `https://www.pracuj.pl/praca/${encodeURIComponent(careerName.toLowerCase().replace(/\s+/g, '-'))};kw`,
        salary: career.zarobki ? `${career.zarobki.min} - ${career.zarobki.max} ${career.zarobki.waluta}` : 'Brak danych'
      };
    });
    
    setJobs(generatedJobs);
  }, [analysis]);

  if (!jobs.length) {
    return null;
  }

  // Dla wersji free pokazujemy tylko pierwszą ofertę, reszta jest zablurowana
  const visibleJobs = isPremium ? jobs : jobs.slice(0, 1);
  const hiddenJobs = isPremium ? [] : jobs.slice(1);

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-xl p-6 relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
          Aktualne oferty pracy
        </h3>
      </div>
      
      <div className="space-y-4">
        {visibleJobs.map((job, index) => (
          <div 
            key={index} 
            className={`p-4 border rounded-xl ${index === 0 ? 'border-purple-200 bg-purple-50' : 'border-gray-200'}`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-gray-900">{job.title}</h4>
                {job.company && <p className="text-sm text-gray-600 mt-1">{job.company}</p>}
                {job.salary && <p className="text-sm text-green-600 mt-1">{job.salary}</p>}
              </div>
              <a 
                href={job.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800"
              >
                <ExternalLink className="w-4 h-4" />
                Sprawdź
              </a>
            </div>
          </div>
        ))}
        
        {/* Zablurowane oferty dla użytkowników darmowych */}
        {!isPremium && hiddenJobs.length > 0 && (
          <div className="relative">
            {hiddenJobs.slice(0, 2).map((job, index) => (
              <div 
                key={`hidden-${index}`}
                className="p-4 border border-gray-200 rounded-xl mb-4 opacity-60 blur-[4px]"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">{job.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{job.company}</p>
                    <p className="text-sm text-green-600 mt-1">{job.salary}</p>
                  </div>
                  <div className="inline-flex items-center gap-1 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    Sprawdź
                  </div>
                </div>
              </div>
            ))}
            
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="bg-white/90 p-4 rounded-xl shadow-sm text-center">
                <Lock className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Odblokuj {hiddenJobs.length} więcej dopasowanych ofert pracy
                </p>
                <button
                  onClick={() => navigate('/plany')}
                  className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Przejdź do Premium
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default JobRecommendations;
